import { useMemo } from 'react';
import * as S from './styles';
import type { PaginationVariant } from './styles';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  maxVisible?: number;
  style?: React.CSSProperties;
  align?: 'left' | 'center' | 'right';
  variant?: PaginationVariant;
}

// Ghost variant arrow (기존 스타일)
const GhostArrowLeft = () => (
  <svg
    width="6"
    height="10"
    viewBox="0 0 6 10"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4.7002 8.69995L0.700195 4.69995L4.7002 0.699951"
      stroke="white"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const GhostArrowRight = () => (
  <svg
    width="6"
    height="10"
    viewBox="0 0 6 10"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M0.700195 8.69995L4.7002 4.69995L0.700195 0.699951"
      stroke="white"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Solid variant arrow (Figma 디자인 기반)
const SolidArrowLeft = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ transform: 'rotate(90deg)' }}
  >
    <path
      d="M7 9L10 12L13 9"
      stroke="#B3B5BD"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SolidArrowRight = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ transform: 'rotate(-90deg)' }}
  >
    <path
      d="M7 9L10 12L13 9"
      stroke="#B3B5BD"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  maxVisible = 5,
  style,
  align = 'right',
  variant = 'ghost',
}: PaginationProps) {
  const pageNumbers = useMemo(() => {
    const pages: number[] = [];
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    const end = Math.min(totalPages, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  }, [currentPage, totalPages, maxVisible]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      onPageChange(page);
    }
  };

  if (totalPages <= 1) {
    return null;
  }

  const ArrowLeft = variant === 'solid' ? SolidArrowLeft : GhostArrowLeft;
  const ArrowRight = variant === 'solid' ? SolidArrowRight : GhostArrowRight;

  return (
    <S.Pagination style={style} $align={align} $variant={variant}>
      <S.PaginationButton
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        $variant={variant}
      >
        <ArrowLeft />
      </S.PaginationButton>

      <S.PageNumbers $variant={variant}>
        {pageNumbers.map((page) => (
          <S.PageNumber
            key={page}
            $active={page === currentPage}
            $variant={variant}
            onClick={() => handlePageChange(page)}
          >
            {page}
          </S.PageNumber>
        ))}
      </S.PageNumbers>

      <S.PaginationButton
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        $variant={variant}
      >
        <ArrowRight />
      </S.PaginationButton>
    </S.Pagination>
  );
}
